import { ColDef, RowClickedEvent, themeAlpine } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Search } from "lucide-react";
import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useImmer } from "use-immer";

import { IgButton, IgTableWrapper, IgTextField } from "@intelligems/ig-theme";
import { AgencyDto } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { useGetAgencies } from "@src/api/agencies/get-all-agencies";
import { SkeletonGroup } from "@src/components/Utils/SkeletonGroup";
import { AgenciesPanel } from "@src/features/admin/pages/agencies/components/Expansions/AgenciesPanel/AgenciesPanel";
import { StatusColumn } from "@src/features/admin/pages/agencies/components/Tables/AgenciesTable/renderers/StatusColumns";
import { openDrawer } from "@src/store/slices/drawers";
import { theme } from "@src/theme";

const myTheme = themeAlpine.withParams({
  fontFamily: theme.typography.fontFamily,
});

export const AgenciesTable = () => {
  const [filters, setFilters] = useImmer<{ search: string }>({ search: "" });
  const agencies = useGetAgencies();
  const dispatch = useDispatch();
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
    };
  }, []);

  const columnDefs: ColDef<AgencyDto & { "": "" }>[] = useMemo(
    () => [
      {
        headerName: "Name",
        field: "name",
        sortable: true,
        filter: "agTextColumnFilter",
        cellStyle: { justifyContent: "start" },
      },
      {
        headerName: "Status",
        field: "",
        maxWidth: 350,
        sortable: false,
        cellStyle: { justifyContent: "end" },
        cellRenderer: StatusColumn,
      },
      {
        headerName: "",
        showRowGroup: true,
        width: 20,
        maxWidth: 50,
        cellStyle: { justifyContent: "end" },
        cellRenderer: "agGroupCellRenderer",
      },
    ],
    []
  );

  const onRowClick = (event: RowClickedEvent<AgencyDto>) => {
    event.node.setExpanded(!event.node.expanded);
  };

  const data = useMemo(() => {
    if (agencies.isPending) {
      return [];
    }

    const resp = agencies.data?.agencies || [];

    if (filters.search) {
      return resp.filter((agency) => {
        return (
          agency.name.toLowerCase().includes(filters.search.toLowerCase()) ||
          agency.id.toLowerCase().includes(filters.search.toLowerCase())
        );
      });
    }

    return resp;
  }, [filters, agencies.isLoading, agencies.data]);

  if (!agencies.data) {
    return (
      <Grid item>
        <SkeletonGroup lines={5} />
      </Grid>
    );
  }

  return (
    <Grid
      container
      item
      direction={"column"}
      rowSpacing={2}
      justifyContent={"space-between"}
    >
      <Grid container item justifyContent={"space-between"} columnSpacing={1}>
        <Grid item flex={1}>
          <IgTextField
            fullWidth={true}
            placeholder={"Enter Agency Name or ID"}
            value={filters.search}
            onChange={(e) => {
              setFilters((draft) => {
                draft.search = e.target.value;
              });
            }}
            InputProps={{
              endAdornment: <Search size={16} />,
            }}
          />
        </Grid>
        <Grid item>
          <IgButton
            variant={"contained"}
            onClick={() =>
              dispatch(
                openDrawer({
                  type: "putAgency",
                  mode: "create",
                  agency: null,
                })
              )
            }
          >
            Create Agency
          </IgButton>
        </Grid>
      </Grid>

      <Grid item>
        <IgTableWrapper height={"80vh"}>
          <AgGridReact<AgencyDto>
            theme={myTheme}
            rowData={data}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            detailCellRenderer={AgenciesPanel}
            masterDetail={true}
            domLayout={"autoHeight"}
            detailRowAutoHeight={true}
            suppressCellFocus={true}
            headerHeight={40}
            onRowClicked={onRowClick}
          />
        </IgTableWrapper>
      </Grid>
    </Grid>
  );
};
